import { ContractPartner, ContractPartnerFormData } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// Contract partner validation schema with translations
const createContractPartnerSchema = (tForms: (key: string) => string) =>
  z
    .object({
      language: z
        .string()
        .min(1, tForms('contractPartner.validation.languageRequired')),
      role: z
        .string()
        .min(1, tForms('contractPartner.validation.roleRequired')),
      isRepresentative: z.boolean(),
      hasDifferentAddress: z.boolean(),
      title: z.string().optional(),
      firstName: z
        .string()
        .min(1, tForms('contractPartner.validation.firstNameRequired')),
      lastName: z
        .string()
        .min(1, tForms('contractPartner.validation.lastNameRequired')),
      phone: z.string().optional(),
      mobile: z.string().optional(),
      email: z
        .string()
        .min(1, tForms('contractPartner.validation.emailRequired'))
        .email(tForms('contractPartner.validation.emailInvalid')),
      street: z.string().optional(),
      houseNumber: z.string().optional(),
      postalCode: z.string().optional(),
      city: z.string().optional(),
      country: z.string().optional(),
      state: z.string().optional(),
    })
    .refine(
      data => {
        // If has different address, these fields are required
        if (data.hasDifferentAddress) {
          return !!(
            data.street &&
            data.houseNumber &&
            data.postalCode &&
            data.city &&
            data.country
          );
        }
        return true;
      },
      {
        message: tForms('contractPartner.validation.addressRequired'),
        path: ['street'], // Will show error on street field
      }
    );

export type ContractPartnerSchemaType = z.infer<
  ReturnType<typeof createContractPartnerSchema>
>;

interface UseContractPartnerFormProps {
  contractPartner?: ContractPartner;
  onSave: (data: ContractPartnerFormData) => void;
  isOpen: boolean;
  tForms: (key: string) => string;
}

export const useContractPartnerForm = ({
  contractPartner,
  onSave,
  isOpen,
  tForms,
}: UseContractPartnerFormProps) => {
  const schema = createContractPartnerSchema(tForms);

  const form = useForm<ContractPartnerSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      language: '',
      role: '',
      isRepresentative: false,
      hasDifferentAddress: false,
      title: '',
      firstName: '',
      lastName: '',
      phone: '',
      mobile: '',
      email: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: '',
      state: '',
    },
  });

  const isEditing = !!contractPartner;

  // Reset form when dialog opens/closes or contract partner changes
  useEffect(() => {
    if (isOpen) {
      if (contractPartner) {
        // Edit mode - populate with existing data
        form.reset({
          language: contractPartner.language || '',
          role: contractPartner.role || '',
          isRepresentative: contractPartner.isRepresentative || false,
          hasDifferentAddress: contractPartner.hasDifferentAddress || false,
          title: contractPartner.title || '',
          firstName: contractPartner.firstName || '',
          lastName: contractPartner.lastName || '',
          phone: contractPartner.phone || '',
          mobile: contractPartner.mobile || '',
          email: contractPartner.email || '',
          street: contractPartner.street || '',
          houseNumber: contractPartner.houseNumber || '',
          postalCode: contractPartner.postalCode || '',
          city: contractPartner.city || '',
          country: contractPartner.country || '',
          state: contractPartner.state || '',
        });
      } else {
        // Add mode - reset to defaults
        form.reset({
          language: '',
          role: '',
          isRepresentative: false,
          hasDifferentAddress: false,
          title: '',
          firstName: '',
          lastName: '',
          phone: '',
          mobile: '',
          email: '',
          street: '',
          houseNumber: '',
          postalCode: '',
          city: '',
          country: '',
          state: '',
        });
      }
    }
  }, [contractPartner, isOpen, form]);

  // Clear address fields when hasDifferentAddress is toggled OFF
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'hasDifferentAddress' && !value.hasDifferentAddress) {
        form.setValue('street', '');
        form.setValue('houseNumber', '');
        form.setValue('postalCode', '');
        form.setValue('city', '');
        form.setValue('country', '');
        form.setValue('state', '');
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = useCallback(
    (data: ContractPartnerSchemaType) => {
      onSave(data as ContractPartnerFormData);
    },
    [onSave]
  );

  return {
    form,
    handleSubmit,
    isEditing,
  };
};
